import { Component, type ReactNode } from "react";

/* ── Shake keyframes (injected inline) ─────────────────────────────────────── */
const shakeKeyframes = `
@keyframes pc-eb-shake {
  0%   { transform: translateX(0); }
  15%  { transform: translateX(-6px); }
  30%  { transform: translateX(6px); }
  45%  { transform: translateX(-5px); }
  60%  { transform: translateX(5px); }
  75%  { transform: translateX(-3px); }
  90%  { transform: translateX(3px); }
  100% { transform: translateX(0); }
}
`;

/* ── SectionBoundary ────────────────────────────────────────────────────────── */

export interface SectionBoundaryProps {
  /**
   * Identifies this boundary in logs and test IDs.
   * Example: "booking-calendar" or "artist-gallery"
   */
  name: string;
  children: ReactNode;
  /**
   * Custom fallback rendered when a child throws.
   * When omitted, the default inline error strip is shown.
   */
  fallback?: ReactNode;
  /**
   * Called when a child throws. Use this to report the error to your
   * monitoring service (e.g. Sentry.captureException).
   * When omitted, only a console.error is emitted.
   */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface SectionState {
  hasError: boolean;
}

/**
 * SectionBoundary
 *
 * Inline error boundary for a major panel or section of a page.
 * When a child throws, renders a compact error strip with a Retry affordance
 * so the rest of the page stays interactive.
 *
 * Wire up error reporting via the `onError` prop:
 * ```tsx
 * <SectionBoundary name="gallery" onError={(err, info) => captureException(err, info)}>
 *   <Gallery />
 * </SectionBoundary>
 * ```
 */
export class SectionBoundary extends Component<SectionBoundaryProps, SectionState> {
  constructor(props: SectionBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): Partial<SectionState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`SectionBoundary[${this.props.name}] caught:`, error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 py-3 my-2 rounded-lg border border-red-500/30 bg-red-500/[0.04] text-red-200/80 text-xs"
          data-testid={`section-error-fallback-${this.props.name}`}
          role="alert"
        >
          <div className="flex items-center gap-2">
            <span aria-hidden>⚠</span>
            <span>This section couldn't load — refresh to retry.</span>
          </div>
          <button
            type="button"
            onClick={this.handleRetry}
            className="px-3 py-1.5 rounded-md bg-red-500/15 hover:bg-red-500/25 text-red-100 uppercase tracking-wider text-[10px] font-semibold min-h-[44px] min-w-[44px]"
            data-testid={`button-section-retry-${this.props.name}`}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ── WidgetErrorBoundary ────────────────────────────────────────────────────── */

export interface WidgetErrorBoundaryProps {
  children: ReactNode;
  /**
   * Human-readable label shown in the fallback ("Widget could not load").
   * Defaults to "Widget".
   */
  label?: string;
  /**
   * Called when a child throws. Use this to report the error to your
   * monitoring service.
   */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface WidgetState {
  hasError: boolean;
}

/**
 * WidgetErrorBoundary
 *
 * Minimal error boundary for dashboard widgets and small UI panels.
 * Shows a compact "could not load" pill — does not offer retry.
 *
 * @example
 * <WidgetErrorBoundary label="Analytics panel" onError={captureException}>
 *   <AnalyticsPanel />
 * </WidgetErrorBoundary>
 */
export class WidgetErrorBoundary extends Component<WidgetErrorBoundaryProps, WidgetState> {
  constructor(props: WidgetErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): Partial<WidgetState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("WidgetErrorBoundary caught:", error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-white/10 bg-white/[0.03] text-white/40 text-xs"
          data-testid="widget-error-fallback"
        >
          <span>⚠</span>
          <span>{this.props.label ?? "Widget"} could not load</span>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ── ErrorBoundary (full-page) ──────────────────────────────────────────────── */

export interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Called when a child throws. Use this to report the error to your
   * monitoring service (e.g. Sentry).
   */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  shaking: boolean;
}

/**
 * ErrorBoundary
 *
 * Full-page error boundary. When a child throws, replaces the page with a
 * centred error UI that includes a shake-animated "Try Again" button.
 *
 * Wire up error reporting via `onError`:
 * ```tsx
 * <ErrorBoundary onError={(err, info) => captureException(err, info)}>
 *   <App />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private _shakeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, shaking: false };
  }

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  componentWillUnmount() {
    if (this._shakeTimer) clearTimeout(this._shakeTimer);
  }

  handleTryAgain = () => {
    this.setState({ shaking: true });
    this._shakeTimer = setTimeout(() => {
      this.setState({ hasError: false, shaking: false });
    }, 420);
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="flex flex-col items-center justify-center min-h-screen bg-black px-4 text-center"
          data-testid="error-boundary-fallback"
        >
          <style>{shakeKeyframes}</style>
          <div className="max-w-md space-y-6">
            <h1 className="text-3xl font-heading tracking-widest uppercase text-accent">
              Error
            </h1>
            <h2 className="text-xl font-subheading uppercase tracking-wider text-white">
              Something went wrong
            </h2>
            <p className="text-muted-foreground text-sm leading-relaxed font-light">
              Please try refreshing the page. If the problem persists, contact us for support.
            </p>
            <button
              onClick={this.handleTryAgain}
              style={
                this.state.shaking
                  ? { animation: "pc-eb-shake 0.4s ease-in-out" }
                  : undefined
              }
              className="px-8 py-3 bg-accent hover:bg-accent/80 active:scale-95 text-white transition-all text-xs uppercase tracking-[0.2em] font-subheading rounded"
              data-testid="button-try-again"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ── PublicPageErrorBoundary ────────────────────────────────────────────────── */

export interface PublicPageErrorBoundaryProps {
  children: ReactNode;
  /**
   * Current route — sanitized and passed to `onError` for context.
   * Do NOT pass raw URL paths that may contain tokens.
   */
  route?: string;
  /** Optional context label for error grouping (e.g. "booking-flow"). */
  context?: string;
  /**
   * Called when a child throws. Use this to report the error to Sentry.
   */
  onError?: (
    error: Error,
    errorInfo: React.ErrorInfo,
    meta: { route?: string; context?: string },
  ) => void;
  /**
   * Optional async function that returns { phone?, email? } for the studio.
   * When provided and the page crashes, the contact details are displayed
   * so visitors can still reach the business.
   */
  fetchContact?: () => Promise<{ phone?: string | null; email?: string | null }>;
}

interface PublicPageState {
  hasError: boolean;
  shaking: boolean;
  contact: { phone?: string | null; email?: string | null } | null;
  contactFetchFailed: boolean;
}

/**
 * PublicPageErrorBoundary
 *
 * Error boundary for public-facing pages. Shows a friendly retry UI with
 * optional contact info so visitors can reach the shop even when the page crashes.
 *
 * @example
 * <PublicPageErrorBoundary
 *   route={sanitizedPath}
 *   onError={(err, info, meta) => captureException(err, { ...info, ...meta })}
 *   fetchContact={() => fetch("/api/studio-config").then(r => r.json())}
 * >
 *   <BookingPage />
 * </PublicPageErrorBoundary>
 */
export class PublicPageErrorBoundary extends Component<
  PublicPageErrorBoundaryProps,
  PublicPageState
> {
  private _shakeTimer: ReturnType<typeof setTimeout> | null = null;
  private _unmounted = false;

  constructor(props: PublicPageErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, shaking: false, contact: null, contactFetchFailed: false };
  }

  static getDerivedStateFromError(): Partial<PublicPageState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("PublicPageErrorBoundary caught:", error, errorInfo);
    this.props.onError?.(error, errorInfo, {
      route: this.props.route,
      context: this.props.context,
    });
    if (this.props.fetchContact) {
      this.props
        .fetchContact()
        .then((contact) => {
          if (!this._unmounted) this.setState({ contact, contactFetchFailed: false });
        })
        .catch(() => {
          if (!this._unmounted) this.setState({ contactFetchFailed: true });
        });
    }
  }

  componentWillUnmount() {
    this._unmounted = true;
    if (this._shakeTimer) clearTimeout(this._shakeTimer);
  }

  handleTryAgain = () => {
    this.setState({ shaking: true });
    this._shakeTimer = setTimeout(() => {
      this.setState({ hasError: false, shaking: false });
    }, 420);
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="flex flex-col items-center justify-center min-h-screen bg-black px-4 text-center"
          data-testid="public-error-boundary-fallback"
        >
          <style>{shakeKeyframes}</style>
          <div className="max-w-md space-y-6">
            <h1 className="text-3xl font-heading tracking-widest uppercase text-accent">
              Oops
            </h1>
            <h2 className="text-xl font-subheading uppercase tracking-wider text-white">
              Something went wrong
            </h2>
            <p className="text-muted-foreground text-sm leading-relaxed font-light">
              We're sorry for the inconvenience. Please try refreshing the page.
              If the problem persists, reach out and we'll help you out.
            </p>
            {(this.state.contact?.phone || this.state.contact?.email) && (
              <div
                className="space-y-2 text-sm text-white/80"
                data-testid="public-error-contact-info"
              >
                {this.state.contact.phone && (
                  <div>
                    <a
                      href={`tel:${this.state.contact.phone}`}
                      className="text-white hover:text-accent transition-colors underline-offset-4 hover:underline"
                      data-testid="link-public-error-phone"
                    >
                      {this.state.contact.phone}
                    </a>
                  </div>
                )}
                {this.state.contact.email && (
                  <div>
                    <a
                      href={`mailto:${this.state.contact.email}`}
                      className="text-white hover:text-accent transition-colors underline-offset-4 hover:underline"
                      data-testid="link-public-error-email"
                    >
                      {this.state.contact.email}
                    </a>
                  </div>
                )}
              </div>
            )}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={this.handleTryAgain}
                style={
                  this.state.shaking
                    ? { animation: "pc-eb-shake 0.4s ease-in-out" }
                    : undefined
                }
                className="px-8 py-3 bg-accent hover:bg-accent/80 active:scale-95 text-white transition-all text-xs uppercase tracking-[0.2em] font-subheading rounded"
                data-testid="button-public-try-again"
              >
                Try Again
              </button>
              {(this.state.contactFetchFailed ||
                (!this.state.contact?.phone && !this.state.contact?.email)) && (
                <a
                  href="/contact"
                  className="px-8 py-3 border border-white/20 hover:border-white/40 text-white/70 hover:text-white transition-all text-xs uppercase tracking-[0.2em] font-subheading rounded"
                  data-testid="link-public-contact"
                >
                  Contact Us
                </a>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
